import { getBlob, ref as storageRef, ref as refFromURL } from 'firebase/storage';
import { app, storage } from '../firebase';
import { refFromAnyStorageUrl } from './storage-file.service';
import { useEffect, useState } from 'react';

export async function openFileFromStorage(fullUrl: string): Promise<string | null> {
  try {
    const fileRef = refFromAnyStorageUrl(app, fullUrl);
    const blob = await getBlob(fileRef);
    console.log(URL.createObjectURL(blob));
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error opening file from storage:', error);
    return null;
  }
}

export const mapObjectWithStorageURLs = async <T extends { [key: string]: any }>(object: T): Promise<T> => {
  const dataWithURLs = await Promise.all(
    Object.keys(object)
      .filter((key) => {
        const value = object[key as keyof T];
        return value && typeof value === 'string' && value.match(/https:\/\/storage\.googleapis\.com\/[^/]+\/(.+)/);
      })
      .map(async (key: string) => ({ [key]: await openFileFromStorage(object[key]) })),
  );

  const objectDataWithURLs = dataWithURLs.reduce((acc: object, data) => {
    acc = {
      ...acc,
      ...data,
    };
    return acc;
  }, {});

  console.log({ ...object, ...objectDataWithURLs });
  return { ...object, ...objectDataWithURLs } as T;
};

const blobUrlCache = new Map<string, string>();

export async function blobUrlFromStorage(anyUrlOrPath: string | null | undefined): Promise<string | null> {
  if (!anyUrlOrPath) {
    return null;
  }

  if (
    !(
      (anyUrlOrPath.startsWith('http') && anyUrlOrPath.match(/https:\/\/storage\.googleapis\.com\/[^/]+\/(.+)/)) ||
      anyUrlOrPath.startsWith('gs://')
    )
  ) {
    return anyUrlOrPath;
  }

  if (blobUrlCache.has(anyUrlOrPath)) return blobUrlCache.get(anyUrlOrPath)!;

  const fileRef =
    anyUrlOrPath.startsWith('http') || anyUrlOrPath.startsWith('gs://')
      ? refFromURL(storage, anyUrlOrPath)
      : storageRef(storage, anyUrlOrPath);

  const blob = await getBlob(fileRef);
  const url = URL.createObjectURL(blob);
  blobUrlCache.set(anyUrlOrPath, url);
  return url;
}

export function revokeBlobUrl(anyUrlOrPath: string) {
  const u = blobUrlCache.get(anyUrlOrPath);
  if (u) {
    URL.revokeObjectURL(u);
    blobUrlCache.delete(anyUrlOrPath);
  }
}

export function useStorageObjectUrl(anyUrlOrPath?: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!anyUrlOrPath);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!anyUrlOrPath) {
      setUrl(null);
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);

    blobUrlFromStorage(anyUrlOrPath)
      .then((u) => alive && setUrl(u))
      .catch((e) => {
        alive && setError(e);
        setUrl(anyUrlOrPath);
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [anyUrlOrPath]);

  return { url, loading, error };
}
